import {
    addDays,
    addMonths,
    addWeeks,
    endOfDay,
    endOfMonth,
    endOfWeek,
    format,
    startOfDay,
    startOfMonth,
    startOfWeek,
} from 'date-fns';

import { formatDate } from '../../utils';
import { EventCalendarView } from './calendar-state';

// FullCalendar's default firstDay is Sunday; the shared range must match the week the
// calendar tab displays so both tabs show and query the same window.
const WEEK_STARTS_ON = 0;

export const getCalendarRange = (anchorDate: Date, view: EventCalendarView): { fromDate: Date; toDate: Date } => {
    switch (view) {
        case EventCalendarView.TIME_GRID_DAY:
            return { fromDate: startOfDay(anchorDate), toDate: endOfDay(anchorDate) };
        case EventCalendarView.TIME_GRID_WEEK:
            return {
                fromDate: startOfWeek(anchorDate, { weekStartsOn: WEEK_STARTS_ON }),
                toDate: endOfWeek(anchorDate, { weekStartsOn: WEEK_STARTS_ON }),
            };
        case EventCalendarView.DAY_GRID_MONTH:
        default:
            return { fromDate: startOfMonth(anchorDate), toDate: endOfMonth(anchorDate) };
    }
};

export const stepAnchorDate = (anchorDate: Date, view: EventCalendarView, direction: 1 | -1): Date => {
    switch (view) {
        case EventCalendarView.TIME_GRID_DAY:
            return addDays(anchorDate, direction);
        case EventCalendarView.TIME_GRID_WEEK:
            return addWeeks(anchorDate, direction);
        case EventCalendarView.DAY_GRID_MONTH:
        default:
            return addMonths(anchorDate, direction);
    }
};

// Single source of truth for the calendar title's collapsing rules, no matter which path
// computed the range (getCalendarRange here, FullCalendar's viewContent in EventCalendar).
export const formatRangeTitle = (fromDate: Date, toDate: Date, view: EventCalendarView): string => {
    switch (view) {
        case EventCalendarView.DAY_GRID_MONTH:
            return format(fromDate, 'LLLL yyyy');
        case EventCalendarView.TIME_GRID_WEEK: {
            const sameMonth = fromDate.getMonth() === toDate.getMonth();
            const sameYear = fromDate.getFullYear() === toDate.getFullYear();
            if (sameMonth && sameYear) {
                return `${format(fromDate, 'LLLL d')}-${format(toDate, 'd, yyyy')}`;
            }
            if (sameYear) {
                return `${format(fromDate, 'LLLL d')} - ${format(toDate, 'LLLL d, yyyy')}`;
            }
            return `${format(fromDate, 'LLLL d, yyyy')} - ${format(toDate, 'LLLL d, yyyy')}`;
        }
        case EventCalendarView.TIME_GRID_DAY:
        default:
            return formatDate(fromDate);
    }
};

export const formatCalendarRangeHeader = (anchorDate: Date, view: EventCalendarView): string => {
    const { fromDate, toDate } = getCalendarRange(anchorDate, view);
    return formatRangeTitle(fromDate, toDate, view);
};

// Calendar sync only ingests events from now through the next 30 days (refreshed twice a
// day), so a range that starts after that horizon cannot contain any synced events.
const CALENDAR_SYNC_HORIZON_DAYS = 30;

export const isBeyondCalendarSyncHorizon = (fromDate: Date, now: Date): boolean =>
    fromDate > addDays(startOfDay(now), CALENDAR_SYNC_HORIZON_DAYS);
